<script lang="ts">
	import type { DataTypeEnum$options as DataType } from '$houdini'

	import Checkbox from '@isoftdata/svelte-checkbox'
	import Icon from '@isoftdata/svelte-icon'
	import Input, { type InputTypes } from '@isoftdata/svelte-input'
	import Select from '@isoftdata/svelte-select'

	import { klona } from 'klona'
	import { dequal } from 'dequal'
	import { getEventValue } from '@isoftdata/browser-event'
	import { stringToBoolean } from '@isoftdata/utility-string'

	interface Props {
		name: string
		dataType: DataType
		value: string | number | boolean | null
		id: number | null
		choices?: string[]
		required?: boolean
		disabled?: boolean
		defaultChoice?: string
		showLabel?: boolean
		change?: (value: string | number | boolean | null) => void
	}

	let {
		name,
		dataType,
		value = $bindable(),
		id,
		choices = [],
		required = false,
		disabled = false,
		defaultChoice = '',
		showLabel = true,
		change,
	}: Props = $props()

	let modified = $state(false)
	let origValue: string | number | boolean | null = klona(value)

	const hint = $derived(modified ? modifiedHint : undefined)

	function getInputType(dataType: DataType): InputTypes {
		switch (dataType) {
			case 'TEXT':
			case 'AUTOSUGGEST_TEXT':
				return 'text'
			case 'NUMBER':
			case 'CURRENCY':
				return 'number'
			case 'DATE':
				return 'date'
			case 'TIME':
				return 'time'
			case 'DATETIME':
				return 'datetime-local'
			default:
				return 'text'
		}
	}

	function onChange(event: Event) {
		value = getEventValue(event)

		if (dataType === 'BOOLEAN') {
			value = stringToBoolean(value)
		}
		if (typeof origValue === 'string' && dataType === 'BOOLEAN') {
			origValue = stringToBoolean(origValue)
		}
		modified = !dequal(origValue, value)
		change?.(value)
	}

	// init value if not set and we have a default
	if ((value === null || value === '') && defaultChoice) {
		value = defaultChoice
		change?.(defaultChoice)
	}
</script>

{#if dataType === 'CHOICE'}
	<Select
		{hint}
		{required}
		{disabled}
		{showLabel}
		label={name}
		bind:value
		onchange={onChange}
	>
		{#each choices as choice}
			<option value={choice}>{choice}</option>
		{/each}
	</Select>
{:else if dataType === 'AUTOSUGGEST_TEXT' && typeof value !== 'boolean'}
	<Input
		selectOnFocus
		{hint}
		{required}
		{disabled}
		{showLabel}
		label={name}
		list="suggestText{id}List"
		bind:value
		onchange={onChange}
	/>
	<datalist id="suggestText{id}List">
		{#each choices as choice}
			<option value={choice}> </option>
		{/each}
	</datalist>
{:else if dataType === 'BOOLEAN' && (typeof value === 'boolean' || typeof value === 'string')}
	{@const checked = typeof value === 'boolean' ? value : stringToBoolean(value)}
	<Checkbox
		{hint}
		{checked}
		{required}
		{disabled}
		{showLabel}
		label={name}
		type="radio"
		trueLabel="True"
		falseLabel="False"
		onchange={onChange}
	/>
{:else if typeof value !== 'boolean'}
	<Input
		{hint}
		{required}
		{disabled}
		{showLabel}
		label={name}
		type={getInputType(dataType)}
		bind:value
		onchange={onChange}
	/>
{/if}

{#snippet modifiedHint()}
	<Icon
		class="text-warning"
		icon="pencil"
		size="xs"
	/>
{/snippet}
